import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bankService } from "../../services/bankService";
import { useBanks } from "../../query/hooks";
import { queryKeys } from "../../query/keys";
import { useAuth } from "../../contexts/AuthContext";
import {
  PlusIcon,
  TrashIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";

function groupAccount(value) {
  return String(value || "").replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

const BankList = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const banksQuery = useBanks(!!user);
  const banks = banksQuery.data;
  const loading = !!user && banksQuery.isLoading;
  const pickerRef = useRef(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    bankCode: "",
    accountNumber: "",
    accountName: "",
    bankName: "",
  });
  const paystackQuery = useQuery({
    queryKey: ["paystack-banks"],
    queryFn: () => bankService.listPaystackBanks(),
    enabled: showAddForm,
    staleTime: 24 * 60 * 60 * 1000,
  });
  const paystackBanks = useMemo(() => paystackQuery.data || [], [paystackQuery.data]);
  const banksLoading = paystackQuery.isFetching;
  const banksError = paystackQuery.isError
    ? paystackQuery.error?.response?.data?.message || "The bank list could not be loaded."
    : "";
  const [bankQuery, setBankQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState("");

  const savedAccounts = banks?.data?.bankDetails || [];

  useEffect(() => {
    if (banksQuery.isError) setMessage("Error loading bank accounts");
  }, [banksQuery.isError]);

  useEffect(() => {
    const closeMenu = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, []);

  useEffect(() => {
    const digits = formData.accountNumber.replace(/\D/g, "");
    if (!showAddForm || !formData.bankCode || digits.length !== 10) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setResolving(true);
      setResolveError("");
      try {
        const account = await bankService.resolveAccount({
          accountNumber: digits,
          bankCode: formData.bankCode,
        });
        if (cancelled) return;
        setFormData((current) => ({
          ...current,
          accountName: account.accountName,
          bankName: account.bankName,
        }));
      } catch (error) {
        if (cancelled) return;
        setFormData((current) => ({ ...current, accountName: "" }));
        setResolveError(error.response?.data?.message || "Could not verify this account. Check the account number and bank.");
      } finally {
        if (!cancelled) setResolving(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.accountNumber, formData.bankCode, showAddForm]);

  const filteredBanks = useMemo(() => {
    const needle = bankQuery.trim().toLowerCase();
    if (!needle) return paystackBanks;
    return paystackBanks.filter((bank) => bank.name.toLowerCase().includes(needle));
  }, [bankQuery, paystackBanks]);

  const refreshAccounts = () => queryClient.invalidateQueries({ queryKey: queryKeys.banks });

  const resetForm = () => {
    setFormData({ bankCode: "", accountNumber: "", accountName: "", bankName: "" });
    setBankQuery("");
    setMenuOpen(false);
    setResolveError("");
    setResolving(false);
  };

  const selectBank = (bank) => {
    setFormData((current) => ({
      ...current,
      bankCode: bank.code,
      bankName: bank.name,
      accountName: "",
    }));
    setBankQuery("");
    setMenuOpen(false);
    setResolveError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.accountName || resolving || saving) {
      setResolveError("Could not verify this account. Check the account number and bank.");
      return;
    }
    setMessage("");
    setSaving(true);
    try {
      await bankService.addBankAccount({
        bankCode: formData.bankCode,
        accountNumber: formData.accountNumber,
      });
      resetForm();
      setShowAddForm(false);
      await refreshAccounts();
      setMessage("Bank account added successfully!");
    } catch (error) {
      console.error("Error adding bank account:", error);
      setMessage(error.response?.data?.message || "Could not verify this account. Check the account number and bank.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!bankToDelete) return;
    setMessage("");
    try {
      await bankService.deleteBankAccount(bankToDelete);
      await refreshAccounts();
      setMessage("Bank account deleted successfully!");
      setShowDeleteConfirm(false);
      setBankToDelete(null);
    } catch (error) {
      console.error("Error deleting bank account:", error);
      setMessage(error.response?.data?.message || "Error deleting bank account");
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LogoLoader />
      </div>
    );
  }

  const digits = formData.accountNumber.replace(/\D/g, "");
  const step = formData.accountName ? 3 : formData.bankCode && digits.length === 10 ? 2 : formData.bankCode ? 1 : 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-green-700">Payouts</p>
            <h1 className="mt-1 text-2xl font-bold text-gray-900 sm:text-3xl">Bank accounts</h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-gray-600 sm:text-base">
              Sale payments are sent to a verified Nigerian account. The account name is confirmed before it is saved.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setMessage("");
              setShowAddForm(true);
            }}
            className="inline-flex w-full items-center justify-center rounded-xl bg-green-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-green-800 sm:w-auto"
          >
            <PlusIcon className="mr-2 h-5 w-5" />
            Add bank account
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${
            message.includes("successfully")
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {message.includes("successfully") ? (
            <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          ) : (
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          )}
          <p>{message}</p>
        </div>
      )}

      {showAddForm && (
        <section className="overflow-visible rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 sm:px-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Add a payout account</h2>
              <p className="mt-1 text-sm text-gray-500">Choose the bank, then enter the 10-digit account number.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                resetForm();
              }}
              className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close add bank form"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.8fr)] lg:items-start sm:p-6">
            <div className="order-1 space-y-5 lg:col-start-1">
              <div ref={pickerRef} className="relative">
                <label htmlFor="bankSearch" className="mb-2 block text-sm font-medium text-gray-800">
                  Bank
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    id="bankSearch"
                    type="text"
                    autoComplete="off"
                    value={menuOpen ? bankQuery : formData.bankName}
                    onFocus={() => {
                      setMenuOpen(true);
                      setBankQuery("");
                    }}
                    onChange={(event) => {
                      setBankQuery(event.target.value);
                      setMenuOpen(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.preventDefault();
                      if (event.key === "Escape") setMenuOpen(false);
                    }}
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-3 text-base text-gray-900 outline-none ring-green-700 placeholder:text-gray-400 focus:border-green-700 focus:ring-2"
                    placeholder="Search GTBank, OPay, Access…"
                  />
                </div>
                {menuOpen && (
                  <div className="absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
                    {banksLoading && <p className="px-4 py-3 text-sm text-gray-500">Loading banks…</p>}
                    {!banksLoading && banksError && (
                      <p className="px-4 py-3 text-sm text-red-700">{banksError}</p>
                    )}
                    {!banksLoading && !banksError && filteredBanks.length === 0 && (
                      <p className="px-4 py-3 text-sm text-gray-500">No bank matches that name.</p>
                    )}
                    {!banksLoading && filteredBanks.map((bank) => {
                      const selected = bank.code === formData.bankCode;
                      return (
                        <button
                          key={bank.code}
                          type="button"
                          onClick={() => selectBank(bank)}
                          className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-green-50 ${
                            selected ? "bg-green-50 font-semibold text-green-800" : "text-gray-800"
                          }`}
                        >
                          <span>{bank.name}</span>
                          {selected && <CheckCircleIcon className="h-4 w-4 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label htmlFor="accountNumber" className="text-sm font-medium text-gray-800">
                    Account number
                  </label>
                  <span className={`text-xs font-medium ${digits.length === 10 ? "text-green-700" : "text-gray-400"}`}>
                    {digits.length}/10
                  </span>
                </div>
                <input
                  type="text"
                  id="accountNumber"
                  inputMode="numeric"
                  autoComplete="off"
                  required
                  maxLength={10}
                  value={formData.accountNumber}
                  onChange={(event) => {
                    const accountNumber = event.target.value.replace(/\D/g, "").slice(0, 10);
                    setFormData((current) => ({ ...current, accountNumber, accountName: "" }));
                    setResolveError("");
                  }}
                  className="w-full rounded-xl border border-gray-300 px-3 py-3 text-base tracking-widest text-gray-900 outline-none ring-green-700 placeholder:tracking-normal placeholder:text-gray-400 focus:border-green-700 focus:ring-2"
                  placeholder="10-digit NUBAN"
                />
              </div>

              <div
                className={`rounded-xl border px-4 py-3 ${
                  resolveError
                    ? "border-red-200 bg-red-50"
                    : formData.accountName
                      ? "border-green-200 bg-green-50"
                      : "border-gray-200 bg-gray-50"
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Account name</p>
                <p className={`mt-1 text-base font-medium ${resolveError ? "text-red-800" : "text-gray-900"}`}>
                  {resolving
                    ? "Checking this account…"
                    : resolveError || formData.accountName || "The name appears after the bank and number are checked."}
                </p>
              </div>
            </div>

            <div className="order-3 flex flex-col-reverse gap-3 sm:flex-row lg:col-start-1 lg:row-start-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!formData.accountName || resolving || saving}
                  className="w-full rounded-xl bg-green-700 px-4 py-3 text-sm font-semibold text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                  {saving ? "Saving…" : "Save account"}
                </button>
              </div>

            <aside className="order-2 rounded-2xl bg-gray-950 p-5 text-white lg:order-none lg:col-start-2 lg:row-span-2 lg:row-start-1">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-green-300">Preview</p>
                <ShieldCheckIcon className="h-5 w-5 text-green-300" />
              </div>
              <p className="mt-6 text-sm text-gray-300">{formData.bankName || "Bank"}</p>
              <p className="mt-2 font-mono text-xl tracking-wider">
                {digits ? groupAccount(digits) : "•••• •••• ••"}
              </p>
              <p className="mt-4 text-lg font-semibold">
                {formData.accountName || "Account name"}
              </p>
              <p className="mt-6 text-sm leading-6 text-gray-300">
                {step === 3
                  ? "This name matches the account. You can save it."
                  : "Jhonks checks the number with the bank before the account is stored."}
              </p>
              <ol className="mt-6 space-y-2 text-sm text-gray-300">
                <li className={step >= 1 ? "text-white" : ""}>1. Select the bank</li>
                <li className={digits.length === 10 ? "text-white" : ""}>2. Enter 10 digits</li>
                <li className={step === 3 ? "text-white" : ""}>3. Confirm the account name</li>
              </ol>
            </aside>
          </form>
        </section>
      )}

      {savedAccounts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 text-green-700">
            <CreditCardIcon className="h-7 w-7" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">No payout account yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-600">
            Add the account that should receive payment when a sale is completed.
          </p>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="mt-6 inline-flex items-center rounded-xl bg-green-700 px-4 py-3 text-sm font-semibold text-white hover:bg-green-800"
          >
            <PlusIcon className="mr-2 h-5 w-5" />
            Add your first account
          </button>
        </div>
      ) : (
        <section>
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Saved accounts</h2>
            <p className="text-sm text-gray-500">{savedAccounts.length}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {savedAccounts.map((bank) => (
              <article key={bank._id} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-700">
                    <CreditCardIcon className="h-6 w-6" />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setBankToDelete(bank._id);
                      setShowDeleteConfirm(true);
                    }}
                    className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="Delete bank account"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
                <h3 className="mt-4 text-base font-semibold text-gray-900">{bank.bankName}</h3>
                <p className="mt-1 text-sm text-gray-600">{bank.accountName}</p>
                <p className="mt-3 font-mono text-sm tracking-wider text-gray-800">{groupAccount(bank.accountNumber)}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
        <h3 className="text-sm font-semibold text-gray-900">Before you save an account</h3>
        <ul className="mt-3 grid gap-2 text-sm leading-6 text-gray-600 sm:grid-cols-2">
          <li>Use a 10-digit NUBAN. The name must match the bank record.</li>
          <li>You can keep more than one account and delete one you no longer use.</li>
        </ul>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
                <ExclamationTriangleIcon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete this account?</h3>
            </div>
            <p className="mt-3 text-sm leading-6 text-gray-600">
              Payments will no longer be sent to this account. This cannot be undone.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setBankToDelete(null);
                }}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 sm:w-auto"
              >
                Keep account
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="w-full rounded-xl bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700 sm:w-auto"
              >
                Delete account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankList;
